import { observer } from 'mobx-react';
import { Box } from '@material-ui/core';
import { lighten, makeStyles } from '@material-ui/core/styles';
import { useStore } from '@roc/client-portal-shared/hooks';
import { platformTokens } from '@roc/feature-app-core';

// Platform Capital brand purple (rgb 133, 45, 202)
const PLATFORM_CAPITAL_PURPLE = '#852DCA';

// tokens are read inside makeStyles (not at module load) because of a circular import with feature-app-core
const useStyles = makeStyles(theme => {
  const { color, radius } = platformTokens;
  return {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1, 2, 1, 1),
      border: `1px solid ${color.neutral100}`,
      borderRadius: radius.lg,
      backgroundColor: color.surface,
      maxWidth: '100%',
    },
    iconBox: {
      flexShrink: 0,
      width: 40,
      height: 40,
      borderRadius: radius.md,
      backgroundColor: lighten(PLATFORM_CAPITAL_PURPLE, 0.9),
      color: PLATFORM_CAPITAL_PURPLE,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: {
      width: 20,
      height: 20,
    },
    text: {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    label: {
      fontSize: 12,
      lineHeight: '16px',
      color: color.subtle,
    },
    value: {
      fontSize: 15,
      lineHeight: '20px',
      fontWeight: 600,
      color: color.heading,
    },
  };
});

/** "Loan Funding" info card shown next to quote / loan submission page titles. */
export const LoanFundingCard = ({ value }: { value: string }) => {
  const classes = useStyles();
  return (
    <Box className={classes.container}>
      <Box className={classes.iconBox}>
        <CapitalProviderIcon className={classes.icon} />
      </Box>
      <Box className={classes.text}>
        <Box className={classes.label}>Loan Funding</Box>
        <Box className={classes.value}>{value}</Box>
      </Box>
    </Box>
  );
};

export const PlatformCapitalProvider = observer(() => {
  const { globalStore } = useStore();

  if (!globalStore.userFeatures.platformLoanSubmissionEnabled) {
    return null;
  }

  return <LoanFundingCard value="Platform Capital" />;
});

const CapitalProviderIcon = ({ className }) => {
  return (
    <svg className={className} width="30" height="31" viewBox="0 0 30 31">
      <path d="M6.66667 12H4V21.3333H6.66667V12Z" fill="currentColor" />
      <path d="M14.6667 12H12V21.3333H14.6667V12Z" fill="currentColor" />
      <path
        d="M26.6667 6.66667L13.3333 0L0 6.66667V9.33333H26.6667V6.66667Z"
        fill="currentColor"
      />
      <path
        d="M0 24V26.6667H16.5333C16.2533 25.8133 16.1067 24.92 16.0533 24H0Z"
        fill="currentColor"
      />
      <path
        d="M22.6667 15.0133V12H20V16.3467L22.6667 15.0133Z"
        fill="currentColor"
      />
      <path
        d="M24 17.3333L18.6667 20V23.4C18.6667 26.76 20.9467 29.9067 24 30.6667C27.0533 29.9067 29.3333 26.76 29.3333 23.4V20L24 17.3333ZM23.04 26.6667L20.3333 23.96L21.7467 22.5467L23.04 23.84L26.2533 20.6667L27.6667 22.08L23.04 26.6667Z"
        fill="currentColor"
      />
    </svg>
  );
};
